import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, CalendarDays, Loader2, Megaphone, Search, Sparkles, Users,
} from "lucide-react";
import {
  aiSearchKindLabel,
  fetchAdminSearch,
  fetchAiSearch,
  normalizeSearchRow,
} from "@/lib/ai-search-api";

const SCOPE_OPTIONS = [
  { key: "all", label: "All", scopes: null },
  { key: "attendees", label: "Attendees", scopes: ["attendees"] },
  { key: "help", label: "Help", scopes: ["help"] },
  { key: "comms", label: "Comms", scopes: ["comms"] },
  { key: "events", label: "Events", scopes: ["events"] },
];

const EXAMPLES = [
  "badge printing setup",
  "welcome email template",
  "pending approval attendees",
];

function KindIcon({ kind }) {
  const size = 14;
  if (kind === "attendee" || kind === "speaker") return <Users size={size} />;
  if (kind === "help") return <BookOpen size={size} />;
  if (kind === "comms") return <Megaphone size={size} />;
  if (kind === "event") return <CalendarDays size={size} />;
  return <Search size={size} />;
}

export default function AdminAiSearchPanel({
  aiEnabled = false,
  eventId = null,
  onNavigate,
  autoFocus = true,
  compact = false,
}) {
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [scopeKey, setScopeKey] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [aiMerged, setAiMerged] = useState(false);
  const [planGated, setPlanGated] = useState(false);

  const activeScope = useMemo(
    () => SCOPE_OPTIONS.find((s) => s.key === scopeKey) || SCOPE_OPTIONS[0],
    [scopeKey],
  );

  useEffect(() => {
    if (!autoFocus) return undefined;
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [autoFocus]);

  const runSearch = useCallback(async (term, scope) => {
    const q = String(term || "").trim();
    if (q.length < 1) {
      setItems([]);
      setAiMerged(false);
      setPlanGated(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    const controller = new AbortController();
    try {
      if (aiEnabled && scope.scopes) {
        const scopes = [...scope.scopes];
        if (scopes.includes("attendees") && eventId) {
          const result = await fetchAiSearch(q, {
            scopes,
            eventId,
            limit: 16,
            signal: controller.signal,
          });
          if (result.planGated) {
            setPlanGated(true);
            setItems([]);
            setAiMerged(false);
            return;
          }
          setPlanGated(false);
          setAiMerged(true);
          setItems(result.items.map(normalizeSearchRow));
          return;
        }
        const result = await fetchAiSearch(q, {
          scopes,
          eventId: scopes.includes("attendees") ? eventId : null,
          limit: 16,
          signal: controller.signal,
        });
        if (result.planGated) {
          setPlanGated(true);
          setItems([]);
          setAiMerged(false);
          return;
        }
        setPlanGated(false);
        setAiMerged(true);
        setItems(result.items.map(normalizeSearchRow));
        return;
      }

      const result = await fetchAdminSearch(q, { signal: controller.signal });
      setPlanGated(false);
      setAiMerged(result.aiMerged);
      setItems(result.items.map(normalizeSearchRow));
    } catch (err) {
      if (err?.name === "AbortError") return;
      setError(err?.message || "Search failed");
      setItems([]);
    } finally {
      setLoading(false);
    }
    return () => controller.abort();
  }, [aiEnabled, eventId]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setItems([]);
      setAiMerged(false);
      setPlanGated(false);
      setError("");
      return undefined;
    }
    const t = window.setTimeout(() => {
      runSearch(q, activeScope);
    }, 220);
    return () => window.clearTimeout(t);
  }, [query, activeScope, runSearch]);

  const handleResultClick = () => {
    onNavigate?.();
    setQuery("");
    setItems([]);
  };

  return (
    <div className={compact ? "space-y-3" : "space-y-5"} data-testid="ai-search-panel">
      <div className="space-y-2">
        <div className="relative">
          <Sparkles
            size={16}
            className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${aiEnabled ? "text-[var(--primary)]" : "text-[var(--text-tertiary)]"}`}
            aria-hidden
          />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={aiEnabled ? "Ask in plain language — attendees, help, comms…" : "Search workspace (upgrade for AI semantic search)…"}
            className="search-input search-input--ai w-full bg-[var(--bg-surface)] border border-[var(--border-default)] text-sm py-3 focus:outline-none focus:border-[var(--primary)]"
            data-testid="ai-search-input"
          />
          {loading ? (
            <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[var(--text-tertiary)] pointer-events-none" />
          ) : (
            <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none" aria-hidden />
          )}
        </div>
        <p className="text-[11px] font-mono text-[var(--text-tertiary)]">
          {aiEnabled
            ? "Semantic search across attendees, help articles, email templates, and events."
            : "Keyword search only on your plan. Upgrade to Studio for AI-powered semantic search."}
        </p>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Search scopes">
        {SCOPE_OPTIONS.map((scope) => (
          <button
            key={scope.key}
            type="button"
            role="tab"
            aria-selected={scopeKey === scope.key}
            className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border transition ${
              scopeKey === scope.key
                ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/5"
                : "border-[var(--border-default)] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
            }`}
            onClick={() => setScopeKey(scope.key)}
          >
            {scope.label}
          </button>
        ))}
      </div>

      {!query.trim() && (
        <div className="flex flex-wrap gap-2">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              className="text-xs px-2.5 py-1 border border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--primary)]/40"
              onClick={() => setQuery(ex)}
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {planGated ? (
        <div className="surface p-4 text-sm text-[var(--text-secondary)] border border-[var(--border-default)]">
          AI search requires a Studio plan or higher.{" "}
          <Link to="/admin/billing" className="text-[var(--primary)] underline underline-offset-2" onClick={handleResultClick}>
            View plans
          </Link>
        </div>
      ) : null}

      {error ? (
        <div className="text-sm text-red-400 font-mono">{error}</div>
      ) : null}

      {query.trim() && !loading && !error && !planGated ? (
        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
          {items.length} result{items.length === 1 ? "" : "s"}
          {aiMerged ? " · AI enhanced" : ""}
        </p>
      ) : null}

      <div className={`overflow-auto border border-[var(--border-default)] ${compact ? "max-h-72" : "max-h-[min(52vh,520px)]"}`}>
        {items.map((row) => (
          <Link
            key={row.id}
            to={row.href}
            className="flex items-start gap-3 px-3 py-2.5 border-b border-[var(--border-default)] hover:bg-[var(--bg-elev)] last:border-b-0"
            onClick={handleResultClick}
          >
            <span className="mt-0.5 text-[var(--text-tertiary)]">
              <KindIcon kind={row.kind} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-[var(--text-primary)] truncate">{row.title}</span>
                <span className="text-[9px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                  {aiSearchKindLabel(row.kind)}
                </span>
                {row.ai ? (
                  <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[var(--primary)]/40 text-[var(--primary)]">
                    AI
                  </span>
                ) : null}
              </span>
              {row.subtitle ? (
                <span className="block text-[10px] font-mono text-[var(--text-tertiary)] truncate mt-0.5">
                  {row.subtitle}
                </span>
              ) : null}
            </span>
          </Link>
        ))}
        {query.trim() && !loading && !error && items.length === 0 && !planGated ? (
          <div className="px-3 py-6 text-sm text-[var(--text-tertiary)] text-center">
            No matches. Try a broader phrase or another scope.
          </div>
        ) : null}
      </div>
    </div>
  );
}
